import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { CheckCircle2, MapPin, Plus } from "lucide-react";

export const ConvertDialog = ({
  open,
  onOpenChange,
  selectedOpp,
  installationDate,
  setInstallationDate,
  customerLocations,
  productLocations,
  setProductLocations,
  createNewLocation,
  setCreateNewLocation,
  newLocation,
  setNewLocation,
  formatCurrency,
  onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-xl text-[#42E695]">
          <CheckCircle2 className="h-6 w-6 inline mr-2" />
          Convertir a Ganada
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <p className="text-slate-300">
          Se crearán los siguientes servicios para el cliente:
        </p>
        
        {/* Services to create */}
        <div className="bg-[#1B1B4B] rounded-lg p-3 space-y-2">
          {selectedOpp?.selected_services?.map((svc, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="text-white">{svc.nombre}</span>
              <span className="text-[#42E695]">{formatCurrency(svc.precio)}/mes</span>
            </div>
          ))}
        </div>
        
        {/* Contract Date */}
        <div>
          <Label className="text-slate-300">Fecha de Contratación *</Label>
          <Input
            type="date"
            value={installationDate}
            onChange={(e) => setInstallationDate(e.target.value)}
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
          />
          <p className="text-xs text-slate-500 mt-1">La fecha de instalación se marcará automáticamente cuando se finalice la provisión.</p>
        </div>
        
        {/* Location Selection */}
        <div className="space-y-3">
          <Label className="text-slate-300 flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Asignación de Ubicaciones
          </Label>
          
          {/* Show products with location assignment */}
          {selectedOpp?.selected_services && selectedOpp.selected_services.length > 0 && (
            <div className="space-y-3 mt-3">
              <p className="text-slate-400 text-sm">Asigna una ubicación a cada producto/servicio:</p>
              {(() => {
                let productIndex = 0;
                return selectedOpp.selected_services.map((svc, svcIndex) => {
                  const qty = svc.cantidad || 1;
                  const items = [];
                  for (let i = 0; i < qty; i++) {
                    const currentIndex = productIndex;
                    items.push(
                      <div key={`${svcIndex}-${i}`} className="flex items-center gap-3 p-3 rounded-lg bg-[#1B1B4B] border border-[#1E1E5F]">
                        <div className="flex-1">
                          <p className="text-white font-medium">{svc.nombre}</p>
                          {qty > 1 && <p className="text-slate-400 text-xs">Unidad {i + 1} de {qty}</p>}
                        </div>
                        <Select
                          value={productLocations[currentIndex] || ""}
                          onValueChange={(value) => setProductLocations({...productLocations, [currentIndex]: value})}
                        >
                          <SelectTrigger className="w-48 bg-[#0A0A35] border-[#1E1E5F] text-white">
                            <SelectValue placeholder="Seleccionar ubicación" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                            {customerLocations.map((loc) => (
                              <SelectItem key={loc.id} value={loc.id} className="text-white">
                                {loc.nombre_sede}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                    productIndex++;
                  }
                  return items;
                });
              })()}
            </div>
          )}
          
          {customerLocations.length === 0 && (
            <p className="text-yellow-400 text-sm mt-2">El cliente no tiene ubicaciones. Crea una a continuación:</p>
          )}
          
          {/* Button to add new location */}
          <Button
            variant="outline"
            onClick={() => setCreateNewLocation(!createNewLocation)}
            className={`w-full mt-3 ${createNewLocation ? "border-[#42E695] bg-[#42E695]/10" : "border-[#1E1E5F]"}`}
          >
            <Plus className="h-4 w-4 mr-2" />
            {createNewLocation ? "Ocultar formulario" : "Crear nueva ubicación"}
          </Button>
          
          {/* New Location Form */}
          {createNewLocation && (
            <div className="bg-[#1B1B4B] p-4 rounded-lg space-y-3 mt-3">
              <div>
                <Label className="text-slate-300">Nombre de la sede *</Label>
                <Input
                  value={newLocation.nombre_sede}
                  onChange={(e) => setNewLocation({ ...newLocation, nombre_sede: e.target.value })}
                  placeholder="Ej: Oficina Principal"
                  className="bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Dirección completa</Label>
                <Input
                  value={newLocation.direccion_completa}
                  onChange={(e) => setNewLocation({ ...newLocation, direccion_completa: e.target.value })}
                  className="bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Ciudad</Label>
                  <Input
                    value={newLocation.ciudad}
                    onChange={(e) => setNewLocation({ ...newLocation, ciudad: e.target.value })}
                    className="bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Código Postal</Label>
                  <Input
                    value={newLocation.codigo_postal}
                    onChange={(e) => setNewLocation({ ...newLocation, codigo_postal: e.target.value })}
                    className="bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#1E1E5F] text-slate-300">
            Cancelar
          </Button>
          <Button onClick={onConfirm} className="bg-[#42E695] text-[#04042C]">
            Confirmar y Crear Servicios
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
